// ── Screen-Space Reflections Effect ──
// Custom pmndrs/postprocessing Effect for screen-space ray-marched reflections.
// Replaces archived 0beqz/screen-space-reflections for Three.js 0.183 compat.

import { Effect, EffectAttribute, BlendFunction } from 'postprocessing';
import * as THREE from 'three';

// ---------------------------------------------------------------------------
// GLSL Fragment Shader
// ---------------------------------------------------------------------------

const fragmentShader = /* glsl */ `
uniform mat4 projMatrix;
uniform mat4 invProjMatrix;
uniform vec2 resolution;
uniform float maxDistance;
uniform float thickness;
uniform float maxRoughness;
uniform float jitter;
uniform float blend;
uniform float blur;
uniform int steps;
uniform int refineSteps;
uniform float missedRayDarken;

// Reconstruct view-space position from depth at the given UV
vec3 getViewPosition(vec2 uv, float d) {
  // d is the raw depth from the depth buffer (0..1, reversed-Z or standard)
  vec4 clipPos = vec4(uv * 2.0 - 1.0, d * 2.0 - 1.0, 1.0);
  vec4 viewPos = invProjMatrix * clipPos;
  return viewPos.xyz / viewPos.w;
}

// Read linear depth at uv (distance from camera along Z)
float getDepth(vec2 uv) {
  return texture2D(depthBuffer, uv).r;
}

// Estimate view-space normal from depth buffer using finite differences
vec3 estimateNormal(vec2 uv) {
  vec2 texel = 1.0 / resolution;
  float d = getDepth(uv);
  float dR = getDepth(uv + vec2(texel.x, 0.0));
  float dU = getDepth(uv + vec2(0.0, texel.y));

  vec3 p0 = getViewPosition(uv, d);
  vec3 pR = getViewPosition(uv + vec2(texel.x, 0.0), dR);
  vec3 pU = getViewPosition(uv + vec2(0.0, texel.y), dU);

  vec3 normal = normalize(cross(pR - p0, pU - p0));
  return normal;
}

// Simple hash for jitter
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

// Project a view-space point to screen UV + depth
vec3 projectToScreen(vec3 viewPos) {
  vec4 clipPos = projMatrix * vec4(viewPos, 1.0);
  clipPos.xyz /= clipPos.w;
  return vec3(clipPos.xy * 0.5 + 0.5, clipPos.z * 0.5 + 0.5);
}

// Ray march in screen space. Returns vec4(hitUV, hitDepth, hit)
// where hit > 0.5 means we found an intersection.
vec4 rayMarch(vec3 origin, vec3 dir) {
  // Project ray start and a point along the ray to screen space
  vec3 startScreen = projectToScreen(origin);
  vec3 endView = origin + dir * maxDistance;
  vec3 endScreen = projectToScreen(endView);

  // Direction in screen space
  vec3 screenDir = endScreen - startScreen;

  // Scale step size so we cover the full distance in 'steps' iterations
  float stepSize = 1.0 / float(steps);

  // Add jitter to reduce banding
  float jitterOffset = jitter * hash(startScreen.xy * resolution) * stepSize;

  vec3 currentScreen = startScreen + screenDir * jitterOffset;
  vec3 stepDelta = screenDir * stepSize;

  for (int i = 0; i < 128; i++) {
    if (i >= steps) break;

    currentScreen += stepDelta;

    // Bail if we go off-screen
    if (currentScreen.x < 0.0 || currentScreen.x > 1.0 ||
        currentScreen.y < 0.0 || currentScreen.y > 1.0) {
      break;
    }

    // Compare ray depth with scene depth
    float sceneDepth = getDepth(currentScreen.xy);
    float rayDepth = currentScreen.z;

    // Hit test: ray is behind the scene surface within thickness tolerance
    float depthDiff = rayDepth - sceneDepth;
    if (depthDiff > 0.0 && depthDiff < thickness) {
      // Binary search refinement
      vec3 refineDelta = stepDelta * 0.5;
      vec3 refinePos = currentScreen - refineDelta;

      for (int j = 0; j < 16; j++) {
        if (j >= refineSteps) break;

        refineDelta *= 0.5;
        float refineSceneDepth = getDepth(refinePos.xy);
        float refineRayDepth = refinePos.z;

        if (refineRayDepth > refineSceneDepth) {
          refinePos -= refineDelta;
        } else {
          refinePos += refineDelta;
        }
      }

      return vec4(refinePos.xy, refinePos.z, 1.0);
    }
  }

  return vec4(0.0, 0.0, 0.0, 0.0);
}

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  float depth = getDepth(uv);

  // Skip sky / far plane
  if (depth >= 1.0 || depth <= 0.0) {
    outputColor = inputColor;
    return;
  }

  // Estimate surface roughness from depth-normal variation.
  // Smooth surfaces (puddles, metal) have consistent normals across
  // neighboring pixels; rough surfaces (concrete, brick) show high
  // normal variance. This approximation lets us skip rough surfaces
  // without a G-buffer roughness channel.
  vec2 texel = 1.0 / resolution;
  vec3 nC = estimateNormal(uv);
  vec3 nR = estimateNormal(uv + vec2(texel.x, 0.0));
  vec3 nU = estimateNormal(uv + vec2(0.0, texel.y));
  float normalVariance = 1.0 - min(dot(nC, nR), dot(nC, nU));
  float estimatedRoughness = clamp(normalVariance * 4.0, 0.0, 1.0);

  // Early-out for rough surfaces (brick, concrete, wood)
  if (estimatedRoughness > maxRoughness) {
    outputColor = inputColor;
    return;
  }

  // Roughness fade: reduce reflection strength as roughness approaches limit
  float roughnessFade = 1.0 - smoothstep(maxRoughness * 0.5, maxRoughness, estimatedRoughness);

  // Reconstruct view-space position and normal
  vec3 viewPos = getViewPosition(uv, depth);
  vec3 viewDir = normalize(viewPos);
  vec3 reflectDir = reflect(viewDir, nC);

  // Skip surfaces facing away from camera (back-faces) or
  // surfaces where the reflection direction would go into the surface
  if (reflectDir.z > 0.0) {
    outputColor = inputColor;
    return;
  }

  // Fresnel: stronger reflections at grazing angles
  float NdotV = max(dot(-viewDir, nC), 0.0);
  float fresnel = pow(1.0 - NdotV, 3.0) * 0.7 + 0.3;

  // Ray march
  vec4 hitResult = rayMarch(viewPos, reflectDir);

  if (hitResult.w > 0.5) {
    vec2 hitUV = hitResult.xy;

    // Edge fade: reduce intensity near screen borders
    vec2 edgeFade = smoothstep(0.0, 0.1, hitUV) * (1.0 - smoothstep(0.9, 1.0, hitUV));
    float screenFade = edgeFade.x * edgeFade.y;

    // Sample the reflected color with blur (box tap for rough surfaces)
    vec4 reflectedColor;
    if (blur > 0.001 && estimatedRoughness > 0.02) {
      // Roughness-proportional blur kernel
      float blurRadius = blur * estimatedRoughness * 4.0;
      vec2 blurStep = texel * blurRadius;
      reflectedColor = texture2D(inputBuffer, hitUV) * 0.4
        + texture2D(inputBuffer, hitUV + vec2(blurStep.x, 0.0)) * 0.15
        + texture2D(inputBuffer, hitUV - vec2(blurStep.x, 0.0)) * 0.15
        + texture2D(inputBuffer, hitUV + vec2(0.0, blurStep.y)) * 0.15
        + texture2D(inputBuffer, hitUV - vec2(0.0, blurStep.y)) * 0.15;
    } else {
      reflectedColor = texture2D(inputBuffer, hitUV);
    }

    // Blend
    float reflectionStrength = blend * screenFade * fresnel * roughnessFade;
    outputColor = mix(inputColor, reflectedColor, reflectionStrength);
  } else {
    // Missed ray: on reflective surfaces (high Fresnel), subtly darken and
    // desaturate to simulate environment absorption where SSR data is
    // unavailable. This avoids a hard cut between reflected and non-reflected
    // areas at screen edges and behind the camera.
    float missStrength = fresnel * roughnessFade * blend * missedRayDarken;
    // Desaturate: convert to luminance and blend toward it
    float luma = dot(inputColor.rgb, vec3(0.2126, 0.7152, 0.0722));
    vec3 desaturated = mix(inputColor.rgb, vec3(luma), 0.3);
    // Darken slightly
    vec3 darkened = desaturated * 0.92;
    outputColor = vec4(mix(inputColor.rgb, darkened, missStrength), inputColor.a);
  }
}
`;

// ---------------------------------------------------------------------------
// SSR Effect Parameters
// ---------------------------------------------------------------------------

export interface SSREffectParams {
  /** Number of ray march steps. More = better quality, higher cost. Default: 20 */
  steps?: number;
  /** Binary search refinement steps after initial hit. Default: 5 */
  refineSteps?: number;
  /** Maximum ray travel distance in view space. Default: 10.0 */
  distance?: number;
  /** Depth thickness tolerance for hit detection. Default: 0.015 */
  thickness?: number;
  /** Maximum roughness for surfaces to receive reflections. Default: 0.3 */
  maxRoughness?: number;
  /** Resolution scale for the effect (0..1). Default: 0.75 */
  resolutionScale?: number;
  /** Jitter amount for ray start to reduce banding (0..1). Default: 0.3 */
  jitter?: number;
  /** Reflection blend strength (0..1). Default: 0.5 */
  blend?: number;
  /** Blur amount for rougher surface reflections (0..1). Default: 0.5 */
  blur?: number;
  /** Missed ray darkening/desaturation strength (0..1). Default: 0.4 */
  missedRayDarken?: number;
}

// ---------------------------------------------------------------------------
// SSR Effect Class
// ---------------------------------------------------------------------------

export class SSREffect extends Effect {
  private camera: THREE.Camera;
  private projMatrixUniform: THREE.Uniform<THREE.Matrix4>;
  private invProjMatrixUniform: THREE.Uniform<THREE.Matrix4>;
  private resolutionUniform: THREE.Uniform<THREE.Vector2>;
  private _resolutionScale: number;

  constructor(camera: THREE.Camera, params: SSREffectParams = {}) {
    const {
      steps = 20,
      refineSteps = 5,
      distance = 10.0,
      thickness = 0.015,
      maxRoughness = 0.3,
      resolutionScale = 0.75,
      jitter = 0.3,
      blend = 0.5,
      blur = 0.5,
      missedRayDarken = 0.4,
    } = params;

    const projMatrix = new THREE.Uniform(new THREE.Matrix4());
    const invProjMatrix = new THREE.Uniform(new THREE.Matrix4());
    const resolution = new THREE.Uniform(new THREE.Vector2(1, 1));

    super('SSREffect', fragmentShader, {
      attributes: EffectAttribute.CONVOLUTION | EffectAttribute.DEPTH,
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, THREE.Uniform>([
        ['projMatrix', projMatrix],
        ['invProjMatrix', invProjMatrix],
        ['resolution', resolution],
        ['maxDistance', new THREE.Uniform(distance)],
        ['thickness', new THREE.Uniform(thickness)],
        ['maxRoughness', new THREE.Uniform(maxRoughness)],
        ['jitter', new THREE.Uniform(jitter)],
        ['blend', new THREE.Uniform(blend)],
        ['blur', new THREE.Uniform(blur)],
        ['steps', new THREE.Uniform(steps)],
        ['refineSteps', new THREE.Uniform(refineSteps)],
        ['missedRayDarken', new THREE.Uniform(missedRayDarken)],
      ]),
    });

    this.camera = camera;
    this.projMatrixUniform = projMatrix;
    this.invProjMatrixUniform = invProjMatrix;
    this.resolutionUniform = resolution;
    this._resolutionScale = resolutionScale;
  }

  /**
   * Called each frame by the postprocessing pipeline.
   * Updates camera matrices so the ray marching uses current view.
   */
  update(
    _renderer: THREE.WebGLRenderer,
    _inputBuffer: THREE.WebGLRenderTarget,
    _deltaTime?: number,
  ): void {
    // Update projection matrices from camera
    const cam = this.camera as THREE.PerspectiveCamera;
    this.projMatrixUniform.value.copy(cam.projectionMatrix);
    this.invProjMatrixUniform.value.copy(cam.projectionMatrixInverse);
  }

  /**
   * Called when the effect composer resizes. Update resolution uniform.
   */
  setSize(width: number, height: number): void {
    const scaledW = Math.max(1, Math.floor(width * this._resolutionScale));
    const scaledH = Math.max(1, Math.floor(height * this._resolutionScale));
    this.resolutionUniform.value.set(scaledW, scaledH);
  }

  /** Get the current resolution scale. */
  get resolutionScale(): number {
    return this._resolutionScale;
  }

  /** Set the resolution scale (0..1). Triggers a resize on next frame. */
  set resolutionScale(value: number) {
    this._resolutionScale = Math.max(0.1, Math.min(1.0, value));
  }

  // -- Parameter setters for runtime quality adjustment --

  setSteps(value: number): void {
    const u = this.uniforms.get('steps');
    if (u) u.value = value;
  }

  setRefineSteps(value: number): void {
    const u = this.uniforms.get('refineSteps');
    if (u) u.value = value;
  }

  setDistance(value: number): void {
    const u = this.uniforms.get('maxDistance');
    if (u) u.value = value;
  }

  setThickness(value: number): void {
    const u = this.uniforms.get('thickness');
    if (u) u.value = value;
  }

  setMaxRoughness(value: number): void {
    const u = this.uniforms.get('maxRoughness');
    if (u) u.value = value;
  }

  setJitter(value: number): void {
    const u = this.uniforms.get('jitter');
    if (u) u.value = value;
  }

  setBlend(value: number): void {
    const u = this.uniforms.get('blend');
    if (u) u.value = value;
  }

  setBlur(value: number): void {
    const u = this.uniforms.get('blur');
    if (u) u.value = value;
  }

  setMissedRayDarken(value: number): void {
    const u = this.uniforms.get('missedRayDarken');
    if (u) u.value = value;
  }
}
